import * as React from 'react';
import { CalendarIcon } from 'components/NewDashboard/icons/CalendarIcon';
import { fetchClientsIfNeeded } from '@pec/aion-ui-core/actions/selectClient';
import { fetchOperationsPastDuePeriodsIfNeeded } from 'actions/operationsReportingToDo';
import { OrganizationFeature } from '@pec/aion-ui-core/interfaces/organization';
import { selectIsChasContractor } from '@pec/aion-ui-core/selectors/organization';
import { ToDoChild, ToDoDescriptor } from '../types';
import { useAppDispatch, useTypedSelector } from '../../../../combineReducers';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

type RouteParams = {
  organizationId: string;
};

export const useOperationsReportingToDo = (): ToDoDescriptor[] => {
  const dispatch = useAppDispatch();
  const { organizationId } = useParams<RouteParams>();
  const { t } = useTranslation('subscriber');
  const isChas = useTypedSelector(selectIsChasContractor);
  const { clients, isFetching: isFetchingClients } = useTypedSelector(state => state.clients);
  const { periods, isFetching: isFetchingPeriods } = useTypedSelector(state => state.operationsReportingToDo);

  React.useEffect(() => {
    if (organizationId && !isChas) {
      dispatch(fetchClientsIfNeeded(organizationId, [OrganizationFeature.OperationalMetrics]));
      dispatch(fetchOperationsPastDuePeriodsIfNeeded(organizationId));
    }
  }, [organizationId, isChas, dispatch]);

  const countsByClientId = (periods || []).reduce<Record<string, number>>((counts, { clientId }) => {
    counts[clientId] = (counts[clientId] || 0) + 1;
    return counts;
  }, {});

  const children: ToDoChild[] = (clients || [])
    .filter(({ id }) => countsByClientId[id] > 0)
    .map(({ id, name }) => ({
      id,
      href: `/organizations/${organizationId}/reporting/operations/clients/${id}`,
      label: t('newDashboard.toDoWidget.operationsClientPastDue', '{{clientName}} ({{count}})', {
        clientName: name,
        count: countsByClientId[id]
      })
    }));

  // Derived from the same clients as `children` so the header count never counts periods
  // belonging to a client that isn't rendered below it (e.g. an unreleased client connection).
  const count = children.reduce((total, { id }) => total + countsByClientId[id], 0);

  return [
    {
      id: 'operationsReporting',
      variant: 'group',
      label: t('newDashboard.toDoWidget.updateOperationsReporting', 'Update Operations reporting'),
      icon: <CalendarIcon />,
      count,
      children,
      isVisible: !isChas && count > 0,
      isFetching: isFetchingClients || isFetchingPeriods
    }
  ];
};
